import React from "react";
import { motion } from "framer-motion";
import { Workflow, ArrowRight } from "lucide-react";
import HeroCTAs from "./HeroCTAs";
import Typewriter from "./Typewriter";

const HERO_IMG =
  "https://static.prod-images.emergentagent.com/jobs/6bec4570-6aa6-48df-994c-3a5fa6a9a14a/images/2744fd4b483fe64ddeea7321c3a84055751ae66085e123c4428e76b9641b0462.png";

export const Hero = () => {
  return (
    <section
      id="inicio"
      className="relative w-full overflow-hidden bg-[#0A192F] py-24 md:py-32"
      data-testid="hero-section"
    >
      {/* Background image */}
      <div className="absolute inset-0">
        <img
          src={HERO_IMG}
          alt="Juventud argentina"
          className="w-full h-full object-cover opacity-60"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0A192F]/90 via-[#0A192F]/65 to-[#0A192F]/95" />
        <div className="absolute inset-0 grid-lines opacity-25 mix-blend-overlay" />
      </div>

      <div
        aria-hidden
        className="absolute -top-10 -right-20 w-72 h-72 bg-[#F5B400]/15 blur-3xl rounded-full"
      />

      <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
        {/* Eyebrow */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.4 }}
          transition={{ duration: 0.6 }}
          className="flex items-center gap-3 mb-7"
        >
          <span className="h-px w-12 bg-[#F5B400]" />
          <span className="font-headings text-xs sm:text-sm tracking-[0.3em] uppercase text-[#F5B400]">
            Capitalismo de Abundancia · Frente Ya
          </span>
        </motion.div>

        {/* PRIMARY headline */}
        <motion.h1
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.8, delay: 0.1 }}
          className="font-headings font-bold uppercase tracking-tight text-white text-4xl sm:text-6xl lg:text-7xl xl:text-8xl leading-[0.95] max-w-5xl min-h-[1.9em] sm:min-h-[2.85em] lg:min-h-[3.8em]"
          data-testid="hero-headline"
        >
          <Typewriter
            segments={[
              { text: "Te vas a " },
              { text: "comprar tu casa", highlight: true },
              { text: " si tenés más de " },
              { text: "18 años", highlight: true },
              { text: "." },
            ]}
            speed={42}
            startDelay={350}
            highlightClassName="text-[#F5B400]"
            cursorClassName="text-[#F5B400]"
            testid="hero-typewriter"
          />
        </motion.h1>

        {/* Featured "Nuestro sistema" button — high visibility, right after H1 */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.7, delay: 0.2 }}
          className="mt-6 flex justify-end"
        >
          <button
            onClick={() => window.dispatchEvent(new Event("open-sistema-modal"))}
            className="group relative inline-flex items-center gap-3 bg-[#F5B400] hover:bg-[#d99f00] text-[#0A192F] font-headings uppercase tracking-wide text-sm sm:text-base px-6 sm:px-8 py-4 shadow-[0_0_0_2px_rgba(245,180,0,0.35),0_10px_30px_-10px_rgba(245,180,0,0.6)] transition-all hover:shadow-[0_0_0_3px_rgba(245,180,0,0.5),0_14px_40px_-10px_rgba(245,180,0,0.8)]"
            data-testid="hero-cta-sistema"
          >
            <span className="absolute -inset-0.5 rounded-none bg-[#F5B400]/40 blur-md animate-pulse pointer-events-none" aria-hidden />
            <Workflow size={20} className="relative z-10" />
            <span className="relative z-10">Mirá cómo funciona nuestro sistema</span>
            <ArrowRight size={16} className="relative z-10 group-hover:translate-x-1 transition-transform" />
          </button>
        </motion.div>

        {/* SECONDARY headline */}
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.7, delay: 0.25 }}
          className="mt-6 font-headings uppercase tracking-tight text-white/95 text-2xl sm:text-3xl lg:text-4xl leading-tight"
          data-testid="hero-secondary"
        >
          <span className="imposible-strike">Im</span><span>posible</span>{" "}
          <span className="text-[#F5B400]">Ya</span>
          <span className="fy-arrow-up ml-0.5">↑</span>
          <span className="mx-3 text-white/30">·</span>
          Cumplís 18 y te comprás tu casa
        </motion.h2>

        {/* Subtitle */}
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.7, delay: 0.35 }}
          className="mt-8 max-w-2xl text-base sm:text-lg text-white/80 leading-relaxed"
          data-testid="hero-subtitle"
        >
          La escasez argentina no es natural. Es una arquitectura que se puede cambiar.
          Patrimonio para cada ciudadano, soberanía operativa y producción con valor agregado.{" "}
          <span className="text-[#F5B400] font-semibold">Menos promesas. Más resultados.</span>
        </motion.p>

        {/* 5 CTAs */}
        <HeroCTAs />

        {/* Bottom strip */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.6, delay: 0.5 }}
          className="mt-14 flex flex-col sm:flex-row sm:items-end gap-6 sm:gap-12 max-w-3xl"
          data-testid="hero-stats"
        >
          <div>
            <div className="font-headings text-4xl sm:text-5xl font-bold text-white tabular-nums">
              100.000 <span className="text-[#F5B400] text-xl align-top">PVA</span>
            </div>
            <div className="text-xs sm:text-sm uppercase tracking-[0.2em] text-white/60 mt-1">
              patrimonio base a los 18
            </div>
          </div>
          <div className="h-px sm:h-12 w-12 sm:w-px bg-white/20" />
          <div className="relative">
            <div className="font-headings text-5xl sm:text-6xl font-bold text-[#F5B400] tabular-nums leading-none">
              0%
            </div>
            <div className="text-xs sm:text-sm uppercase tracking-[0.2em] text-white mt-2 font-semibold">
              Ganancias al salario
            </div>
            <div className="text-[10px] sm:text-xs uppercase tracking-[0.15em] text-white/50 mt-0.5">
              el trabajo no se castiga
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Hero;
